import { createElement, useState } from "react";
import {
    AnnualWheelActivityTableModel,
    AnnualWheelActivityViewModel,
    AnnualWheelMonthEnum,
    AnnualWheelViewModel,
    RecurringIntervalType,
    TaskRawStateEnum,
    TaskStateEnum,
} from "./AnnualWheel.types";
import { useUrlProvider } from "../useUrlProvider";
import { getSettings } from "../common/settingsProvider";
import { createPdfUrl } from "../common/pdfUrlHelper";
import { useDotLegalSnackbar, useStateLocalStorage } from "@dotlegal/dotlegal-ui-components";
import { Trans } from "react-i18next";
import { downloadFile } from "../common/api/downloadFile";
import { useUserContext } from "../auth/userContextProvider/UserContextProvider";
import { useSetPdfPageSize } from "../common/pdfPageSizeHelper";
import { useTranslation } from "../localization/useTranslation";
import { AnnualWheelActivityGeneralType, AnnualWheelPriority } from "./annualWheelDialog/AnnualWheelDialog.types";
import { DotLegalSelectOption } from "../common/components/dotLegalMultiSelect/DotLegalMultiSelect.types";
import { getEnumValues } from "../common/enumOperations";
import { isNullOrWhitespace, setFirstLetterToLowerCase } from "../common/stringOperations";
import { maxNotificationPeriodInDays } from "./RelativeDate.types.ts";
import { exhaustiveGuard } from "../common/utilities.ts";
import { useGroupEntitiesForUserQuery } from "../common/hooks/useSelectableItemQueries.ts";
import { usePlanContext } from "../auth/planProvider/PlanProvider";
import { FilterOption, FilterOptionType } from "@common/components/filterPopover/FilterPopover.types.ts";
import { applyClientFilters, withOperators } from "@common/components/filterBar/filterOperatorMatching.ts";
import { useFilterState } from "@common/hooks/useFilterState.ts";
import { useSeedFilterFromUrl } from "@common/hooks/useSeedFilterFromUrl.ts";
import { useDotLegalQuery } from "@common/hooks/dotLegalReactQuery";
import { usePanes, useRefetchWhenPaneSaves } from "../main/panes/usePanes";
import ActivityPane from "./activityPane/ActivityPane";
import TemplateActivityPane from "./templateActivityPane/TemplateActivityPane";

export function useAnnualWheelDataMapping() {
    useSetPdfPageSize("landscape");
    const snackBar = useDotLegalSnackbar();

    const { customerName, language, permissions } = useUserContext();
    const { platformFeatures, contractManagementFeatures } = usePlanContext();
    const { getAnnualWheelPDFUrl } = useUrlProvider();
    const { translateString, translateAnnualWheelActivityGeneralTypeEnum, translateDate } = useTranslation();

    const { open: openPane } = usePanes();
    const [year, setYear] = useStateLocalStorage("annualWheelYear", new Date().getFullYear());
    const [activityForDeletion, setActivityForDeletion] = useState<AnnualWheelActivityViewModel | undefined>(undefined);
    const [isDownloadingPdf, setIsDownloadingPdf] = useState<boolean>(false);
    const [isDownloadingReport, setIsDownloadingReport] = useState<boolean>(false);
    const [showListView, setShowListView] = useStateLocalStorage("ShowAnnualWheelListView", true);
    const [searchedString, setSearchedString] = useState("");
    const { selectFilters, filterOperators, onFilterChange, pinnedFilterIds, onPinnedFilterIdsChange } = useFilterState("annual-wheel");
    const formatRecurringInterval = useRecurringIntervalFormatting();

    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    const isPdf = Boolean(urlParams.get("isPdf"));
    const pdfYear = Number(urlParams.get("year"));
    const pdfCustomer = urlParams.get("customer");

    const url = `/annualwheel?year=${isPdf ? pdfYear : year}`;
    const { isLoading: isAnnualWheelLoading, data, refetch } = useDotLegalQuery<AnnualWheelViewModel>(url);

    let groupEntitiesForUserQuery = useGroupEntitiesForUserQuery();
    // The group companies decide which activities a stored group company filter keeps, and whether its chip shows at all.
    // Until they arrive the page would show the wrong rows, and a counter click would drop the filter it cannot see yet.
    const isLoading = isAnnualWheelLoading || groupEntitiesForUserQuery.isLoading;

    const { getTaskManagementUrl } = useUrlProvider();

    function refetchData() {
        refetch();
    }

    useRefetchWhenPaneSaves(refetchData);

    let monthsFilterOptions: Array<DotLegalSelectOption> = [];
    let prioritiesFilterOptions: Array<DotLegalSelectOption> = [];
    let statusFilterOptions: Array<DotLegalSelectOption> = [];
    let areasFilterOptions: Array<DotLegalSelectOption> = [];
    let businessAreasFilterOptions: Array<DotLegalSelectOption> = [];
    let responsiblesFilterOptions: Array<DotLegalSelectOption> = [];
    let annualWheelActivityOptions: Array<DotLegalSelectOption> = [];
    let searchableAnnualWheelActivities: Array<AnnualWheelActivityTableModel> = [];
    let annualWheelActivityGeneralTypesFilterOptions: Array<DotLegalSelectOption> = [];
    let contractFilterOptions: Array<DotLegalSelectOption> = [];

    function convertToTableModel(annualWheelActivities: Array<AnnualWheelActivityViewModel>): Array<AnnualWheelActivityTableModel> {
        return annualWheelActivities.map((x) => {
            return {
                ...x,
                dateString: translateDate(x.date),
                priorityString: translateString(setFirstLetterToLowerCase(AnnualWheelPriority[x.priority].toString())),
                statusString: translateString(setFirstLetterToLowerCase(TaskStateEnum[x.taskStatus].toString())),
                businessAreaString: x.businessAreas.map((x) => x.name).join(", "),
                tagsString: x.tags.map((x) => x.name).join(", "),
                complianceAreaString: x.areas.map((x) => x.name).join(", "),
                recurringIntervalString: formatRecurringInterval(x.recurringInterval, x.recurringIntervalType),
                groupCompaniesString: x.groupCompanies.length === 0 ? translateString("all") : x.groupCompanies.map((x) => x.name).join(", "),
                mappedFrameworkString: x.mappedFrameworks.map((x) => x.name).join(", "),
                generalTypeString: x.generalType ? translateAnnualWheelActivityGeneralTypeEnum(x.generalType.toString()) : "",
                responsiblesString: x.responsibles.map((r) => r.name).join(", "),
                subTaskCount: x.subTasks.length,
            };
        });
    }

    if (data) {
        createMonthOptions(data.annualWheelActivities);
        createPriorityOptions(data.annualWheelActivities);
        createStatusOptions(data.annualWheelActivities);
        createAnnualWheelActivityOptions(data.annualWheelActivities);
        businessAreasFilterOptions = data.businessAreaOptions;
        responsiblesFilterOptions = data.userOptions;
        areasFilterOptions = data.areaOptions;
        createAnnualWheelActivityGeneralTypeOptions(data.annualWheelActivityGeneralTypeOptions);

        searchableAnnualWheelActivities = convertToTableModel(data.annualWheelActivities);
        contractFilterOptions = data.contractOptions;
    }

    const hasActivities = isLoading || data!.annualWheelActivities.length > 0;

    const mappedFrameworksOptions = data?.mappedFrameworkOptions ?? [];
    const canSeeMappedFrameworks = mappedFrameworksOptions.length > 0;
    const canSeeContracts =
        contractManagementFeatures.hasContractManagement && contractFilterOptions.length > 0 && permissions.contractManagementPermissions.permissions.read;

    const allGroupCompanyIds = (groupEntitiesForUserQuery.data ?? []).map((x) => x.id);
    // With a single group company every activity belongs to it, so there is nothing to choose between.
    const canFilterOnGroupCompany = allGroupCompanyIds.length > 1;
    // An activity without group companies covers the entire group, so it counts as having all of them.
    const groupCompanyFilterMapping = {
        id: "groupCompanies",
        candidate: (row: AnnualWheelActivityTableModel) => (row.groupCompanies.length === 0 ? allGroupCompanyIds : row.groupCompanies.map((x) => x.id)),
    };
    // A filter stored while the user had several group companies must not keep hiding rows once its chip is gone.
    const groupCompanyFilterMappings = canFilterOnGroupCompany ? [groupCompanyFilterMapping] : [];

    const filteredActivities =
        applyClientFilters(
            searchableAnnualWheelActivities,
            [
                { id: "activities", candidate: (row) => row.id },
                { id: "months", candidate: (row) => row.month.toString() },
                { id: "generalTypes", candidate: (row) => row.generalType?.toString() ?? "" },
                { id: "mappedFrameworks", candidate: (row) => row.mappedFrameworks.map((x) => x.id) },
                { id: "responsibles", candidate: (row) => row.responsibles.map((x) => x.id) },
                { id: "businessAreas", candidate: (row) => row.businessAreas.map((x) => x.id) },
                { id: "areas", candidate: (row) => row.areas.map((x) => x.id) },
                { id: "status", candidate: (row) => row.taskStatus.toString() },
                { id: "priorities", candidate: (row) => row.priority.toString() },
                { id: "contracts", candidate: (row) => row.contractId?.toString() ?? "" },
                ...groupCompanyFilterMappings,
            ],
            selectFilters,
            {},
            filterOperators,
            matchesSearch
        ) ?? [];

    // Like on the activities page, the counters follow the group company filter only: the status filter is what the counters
    // toggle, so letting it narrow them would zero the other counters as soon as one is clicked.
    const activitiesInSelectedGroupCompanies =
        applyClientFilters(searchableAnnualWheelActivities, groupCompanyFilterMappings, selectFilters, {}, filterOperators) ?? [];
    const countWithStatus = (status: TaskStateEnum) => activitiesInSelectedGroupCompanies.filter((x) => x.taskStatus === status).length;
    const taskCounts = {
        overdue: countWithStatus(TaskStateEnum.Overdue),
        planned: countWithStatus(TaskStateEnum.Ready),
        completed: countWithStatus(TaskStateEnum.Completed),
        total: activitiesInSelectedGroupCompanies.length,
    };

    const selectedStatuses = selectFilters.status ?? [];

    // Links from the dashboard and from tasks and documents carry a month or an activity in the URL.
    useSeedFilterFromUrl(true, ["months", "activities"], (params) => {
        const seeded: Array<FilterOption> = [];
        const months = params.get("months");
        if (months) {
            seeded.push(selectOption("months", "month", monthsFilterOptions, months.split(",")));
        }
        const activities = params.get("activities");
        if (activities) {
            seeded.push(selectOption("activities", "activity", annualWheelActivityOptions, activities.toLowerCase().split(",")));
        }
        onFilterChange(seeded);
    });

    function matchesSearch(row: AnnualWheelActivityTableModel) {
        if (searchedString.length === 0) {
            return true;
        }

        const search = searchedString.toLowerCase();
        return (
            row.name.toLowerCase().includes(search) ||
            (row.customId && row.customId.toLowerCase().includes(search)) ||
            row.priorityString.toLowerCase().includes(search) ||
            row.statusString.toLowerCase().includes(search) ||
            row.businessAreas.some((x) => x.name.toLowerCase().includes(search)) ||
            row.areas.some((x) => x.name.toLowerCase().includes(search)) ||
            row.tags.some((x) => x.name.toLowerCase().includes(search)) ||
            row.dateString.toLowerCase().includes(search) ||
            row.responsibles.some((x) => x.name.toLowerCase().includes(search)) ||
            row.recurringIntervalString.toLowerCase().includes(search) ||
            row.groupCompaniesString.toLowerCase().includes(search) ||
            row.generalType?.toString().toLowerCase().includes(search) ||
            row.mappedFrameworks.some((x) => x.name.toLowerCase().includes(search)) ||
            false
        );
    }

    function selectOption(id: string, label: string, fixedData: Array<DotLegalSelectOption>, value = selectFilters[id] ?? []): FilterOption {
        return { id, label: translateString(label), type: FilterOptionType.SelectField, value, fixedData };
    }

    function getFilterOptions(): Array<FilterOption> {
        const filterOptions = [
            selectOption("activities", "activity", annualWheelActivityOptions),
            selectOption("months", "month", monthsFilterOptions),
            selectOption("generalTypes", "type", annualWheelActivityGeneralTypesFilterOptions),
            ...(canSeeMappedFrameworks ? [selectOption("mappedFrameworks", "mappedFrameworks", mappedFrameworksOptions)] : []),
            selectOption("responsibles", "responsibleAndAssignee", responsiblesFilterOptions),
            selectOption("businessAreas", "businessArea", businessAreasFilterOptions),
            ...(platformFeatures.annualWheelComplianceArea ? [selectOption("areas", "area", areasFilterOptions)] : []),
            selectOption("status", "status", statusFilterOptions),
            selectOption("priorities", "priority", prioritiesFilterOptions),
            ...(canSeeContracts ? [selectOption("contracts", "contract", contractFilterOptions)] : []),
            ...(canFilterOnGroupCompany ? [selectOption("groupCompanies", "groupCompany", groupEntitiesForUserQuery.data ?? [])] : []),
        ];

        return withOperators(filterOptions, filterOperators);
    }

    function createMonthOptions(activities: Array<AnnualWheelActivityViewModel>) {
        return getEnumValues(AnnualWheelMonthEnum).forEach((x) => {
            const name = translateString(setFirstLetterToLowerCase(AnnualWheelMonthEnum[x].toString()));
            if (!monthsFilterOptions.some((s) => s.id === x.toString())) {
                const option = activities.map((d) => d.month.toString()).find((t) => t === x.toString());
                if (option) {
                    monthsFilterOptions.push({ name: name, id: x.toString() });
                }
            }
        });
    }

    function createPriorityOptions(activities: Array<AnnualWheelActivityViewModel>) {
        return getEnumValues(AnnualWheelPriority).forEach((x) => {
            const name = translateString(setFirstLetterToLowerCase(AnnualWheelPriority[x].toString()));
            if (!prioritiesFilterOptions.some((s) => s.id === x.toString())) {
                const option = activities.map((d) => d.priority.toString()).find((t) => t === x.toString());
                if (option) {
                    prioritiesFilterOptions.push({ name: name, id: x.toString() });
                }
            }
        });
    }

    function createStatusOptions(activities: Array<AnnualWheelActivityViewModel>) {
        return getEnumValues(TaskStateEnum).forEach((x) => {
            const name = translateString(setFirstLetterToLowerCase(TaskStateEnum[x].toString()));
            if (!statusFilterOptions.some((s) => s.id === x.toString())) {
                const option = activities.map((d) => d.taskStatus.toString()).find((t) => t === x.toString());
                if (option) {
                    statusFilterOptions.push({ name: name, id: x.toString() });
                }
            }
        });
    }

    function createAnnualWheelActivityOptions(activities: Array<AnnualWheelActivityViewModel>) {
        activities
            .filter((item, index, self) => index === self.findIndex((t) => t.id === item.id))
            .forEach((x) => {
                annualWheelActivityOptions.push({ id: x.id, name: x.name });
            });
    }

    function createAnnualWheelActivityGeneralTypeOptions(annualWheelActivityGeneralTypeOptions: Array<AnnualWheelActivityGeneralType>) {
        annualWheelActivityGeneralTypesFilterOptions = annualWheelActivityGeneralTypeOptions.map((x) => {
            const name = translateAnnualWheelActivityGeneralTypeEnum(x.toString());
            return { name: name, id: x.toString() };
        });
    }

    function onBoxCounterClick(status: TaskStateEnum) {
        const clickedStatus = status.toString();
        const nextStatuses = selectedStatuses.includes(clickedStatus)
            ? selectedStatuses.filter((x) => x !== clickedStatus)
            : [...selectedStatuses, clickedStatus];

        onFilterChange(getFilterOptions().map((option) => (option.id === "status" ? { ...option, value: nextStatuses } : option)));
    }

    async function onDownloadPdfClick() {
        const fileName = "annual-wheel";
        setIsDownloadingPdf(true);
        let queryUrl = getSettings().mainSiteBaseUrl + getAnnualWheelPDFUrl() + `?&isPdf=true&year=${year}&customer=${customerName}`;
        await downloadFile(getSettings().apiBaseUrl + "/pdf/generate?url=" + createPdfUrl(queryUrl, language), `${fileName}.pdf`).then((x) => {
            snackBar.show(createElement(Trans, { i18nKey: "pdfDownloadComplete", values: { fileName: fileName } }));
            setIsDownloadingPdf(false);
        });
    }

    function getAnnualWheelExcelExportTranslations(): {
        priority: string;
        status: string;
        title: string;
        deadline: string;
        completedDate: string;
        completedBy: string;
        comment: string;
        description: string;
        documentation: string;
        businessArea: string;
        responsible: string;
        recurrence: string;
        customId: string;
        complianceAreas: string;
        legalEntity: string;
        entireGroup: string;
    } {
        return {
            priority: translateString("priority"),
            status: translateString("status"),
            title: translateString("activity"),
            deadline: translateString("deadline"),
            completedDate: translateString("completedDate"),
            completedBy: translateString("completedBy"),
            comment: translateString("pinnedComments"),
            description: translateString("description"),
            documentation: translateString("requiredDocumentation"),
            businessArea: translateString("businessArea"),
            responsible: translateString("responsible"),
            recurrence: translateString("recurrence") + ` (${translateString("every")} X ${translateString("months")})`,
            customId: "ID",
            complianceAreas: translateString("areas"),
            legalEntity: translateString("groupCompany"),
            entireGroup: translateString("entireGroup"),
        };
    }

    async function onClickDownloadExcelReport() {
        setIsDownloadingReport(true);
        const reportName = translateString("annualWheelReport") + ` ${year}`;
        const fileName = `${reportName} ${new Date().getTime()}.xlsx`;
        const headerTranslations = getAnnualWheelExcelExportTranslations();
        const priorityEnumTranslations: { [K in keyof typeof AnnualWheelPriority]: string } = {
            VeryLow: translateString("veryLow"),
            Low: translateString("low"),
            Moderate: translateString("moderate"),
            High: translateString("high"),
            VeryHigh: translateString("veryHigh"),
        };
        const statusEnumTranslations: { [K in keyof typeof TaskRawStateEnum]: string } = {
            InProgress: translateString("inProgress"),
            Completed: translateString("completed"),
            Ready: translateString("ready"),
            ReadyForApproval: translateString("readyForApproval"),
        };
        const booleanTranslation: { True: string; False: string } = {
            True: translateString("true"),
            False: translateString("false"),
        };
        await downloadFile(getSettings().apiBaseUrl + "/annualWheel/excelExport", fileName, "POST", {
            name: reportName,
            year: year,
            headerTranslations,
            priorityEnumTranslations,
            statusEnumTranslations,
            booleanTranslation,
        });
        setIsDownloadingReport(false);
    }

    function getTaskManagementUrlForMonth(month: AnnualWheelMonthEnum) {
        return getTaskManagementUrl() + "?months=" + month.toString();
    }

    function getStartDate(forMonth?: AnnualWheelMonthEnum) {
        const monthMinus1 = forMonth ? forMonth - 1 : new Date().getMonth();
        const day = forMonth !== undefined ? 15 : new Date().getDate();

        return new Date(year!, monthMinus1, day);
    }

    function openActivity(annualWheelActivityId: string | undefined, forMonth?: AnnualWheelMonthEnum) {
        openPane(ActivityPane, {
            mode: "edit",
            annualWheelActivityId,
            startDate: getStartDate(forMonth),
        });
    }

    function openTemplates() {
        openPane(TemplateActivityPane, { startDate: getStartDate() });
    }

    // Adding from a month cell seeds the new activity's start date with that month rather than today.
    const onMonthAddClicked = (month: AnnualWheelMonthEnum | undefined) => {
        openActivity(undefined, month);
    };

    return {
        isLoading,
        data,
        refetchData,
        openActivity,
        openTemplates,
        year,
        setYear,
        onDownloadPdfClick,
        isDownloadingPdf,
        pdfYear,
        customerName,
        pdfCustomer,
        permissions,
        onClickDownloadExcelReport,
        isDownloadingReport,
        showListView,
        setShowListView,
        getFilterOptions,
        onFilterChange,
        pinnedFilterIds,
        onPinnedFilterIdsChange,
        filteredActivities,
        selectedStatuses,
        taskCounts,
        searchedString,
        setSearchedString,
        onBoxCounterClick,
        getTaskManagementUrlForMonth,
        hasActivities,
        onMonthAddClicked,
        canSeeContracts,
        canDownloadReport: platformFeatures.annualWheelReport,
        canSeeMappedFrameworks,
        activityForDeletion,
        setActivityForDeletion,
    };
}

export function useRecurringIntervalFormatting() {
    const { translateString } = useTranslation();
    return (recurringInterval: number | undefined, recurringIntervalType: RecurringIntervalType | undefined) => {
        if (isNullOrWhitespace(recurringInterval?.toString()) || recurringIntervalType === undefined) {
            return translateString("oneTimeActivity");
        }

        switch (recurringIntervalType) {
            case RecurringIntervalType.Week: {
                return recurringInterval === 1 ? translateString("everyWeek") : translateString("recurEveryXWeek", { repetition: recurringInterval });
            }
            case RecurringIntervalType.Month: {
                return recurringInterval === 1 ? translateString("everyMonth") : translateString("repertEveryXMonth", { repetition: recurringInterval });
            }
            case RecurringIntervalType.Year: {
                return recurringInterval === 1 ? translateString("everyYear") : translateString("recurEveryXYear", { repetition: recurringInterval });
            }
            default:
                exhaustiveGuard(recurringIntervalType);
        }
    };
}

export function useNotificationPeriodInDaysErrorText() {
    const { translateString } = useTranslation();

    return (notificationPeriodInDays: number | undefined) => {
        if (isNullOrWhitespace(notificationPeriodInDays?.toString())) {
            return translateString("fieldRequired");
        }

        if (notificationPeriodInDays! < 1) {
            return translateString("invalidValue");
        }

        if (notificationPeriodInDays! > maxNotificationPeriodInDays) {
            return translateString("annualWheelNotificationPeriodInvalid");
        }

        return "";
    };
}

export function useRecurringIntervalTypeSelectOptions() {
    const { translateString } = useTranslation();

    return [
        {
            id: RecurringIntervalType.Week.toString(),
            name: translateString("weeks"),
        },
        {
            id: RecurringIntervalType.Month.toString(),
            name: translateString("months"),
        },
        {
            id: RecurringIntervalType.Year.toString(),
            name: translateString("years"),
        },
    ];
}
